import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { TESTIMONIALS } from '../data/translations';
import { Language } from '../types';

interface TestimonialsCarouselProps {
  currentLang: Language;
}

export const TestimonialsCarousel: React.FC<TestimonialsCarouselProps> = ({ currentLang }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Number of items per view based on screen size (handled via CSS or responsive logic)
  const totalReviews = TESTIMONIALS.length;

  // Auto-play effect
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalReviews);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, totalReviews]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalReviews) % totalReviews);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalReviews);
  };

  // Touch handlers for swipe
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  };

  return (
    <div
      className="relative w-full overflow-hidden py-4"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Google My Business Rating Header */}
      <div className="mb-8 flex flex-col sm:flex-row items-center justify-center gap-4 bg-white border border-slate-200 p-4 rounded-2xl shadow-sm max-w-xl mx-auto text-center">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">G</div>
          <div className="text-left">
            <div className="flex items-center gap-1 text-amber-500">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-current" />
              ))}
              <span className="text-xs font-bold text-slate-800 ml-1">5.0 / 5.0</span>
            </div>
            <span className="text-xs text-slate-500">Google My Business • Avis vérifiés clients</span>
          </div>
        </div>
        <a
          href="https://share.google/fEyXZvFgRSHJqZECr"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 px-4 py-2 rounded-xl transition inline-flex items-center gap-1.5"
        >
          <span>{currentLang === 'fr' ? 'Voir sur Google ↗' : currentLang === 'nl' ? 'Bekijk op Google ↗' : 'View on Google ↗'}</span>
        </a>
      </div>

      {/* Carousel Track */}
      <div
        className="flex transition-transform duration-500 ease-out gap-6"
        style={{
          transform: `translateX(-${currentIndex * (100 / (window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1))}%)`
        }}
      >
        {TESTIMONIALS.map((rev) => (
          <div
            key={rev.id}
            className="w-full md:w-1/2 lg:w-1/3 shrink-0 px-2"
          >
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-lg hover:shadow-xl transition-all h-full flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-amber-500">
                    {[...Array(rev.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <span className="text-xs font-semibold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">
                    {currentLang === 'fr' ? 'Vérifié' : currentLang === 'nl' ? 'Geverifieerd' : 'Verified'}
                  </span>
                </div>
                <p className="text-slate-700 text-base italic leading-relaxed">
                  "{rev.comment}"
                </p>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                <div>
                  <span className="font-bold text-slate-900 block text-sm">{rev.name}</span>
                  <span className="text-xs text-slate-500">{rev.city}</span>
                </div>
                <span className="bg-slate-100 text-slate-800 text-xs px-3 py-1.5 rounded-xl font-semibold">
                  {rev.service}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-between mt-8 px-4">
        <div className="flex items-center gap-1" role="tablist" aria-label="Pagination des avis clients">
          {TESTIMONIALS.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-full"
              aria-label={`Afficher le témoignage ${idx + 1} sur ${TESTIMONIALS.length}`}
              aria-selected={currentIndex === idx}
              role="tab"
            >
              <span
                className={`h-2.5 rounded-full transition-all block ${
                  currentIndex === idx ? 'w-8 bg-orange-600' : 'w-2.5 bg-slate-300 hover:bg-slate-400'
                }`}
              />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handlePrev}
            className="min-w-[48px] min-h-[48px] p-3 rounded-full bg-white border border-slate-300 shadow-md hover:bg-slate-100 text-slate-800 transition active:scale-95 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-orange-500"
            aria-label="Témoignage précédent"
          >
            <ChevronLeft className="w-6 h-6 text-slate-900" />
          </button>
          <button
            onClick={handleNext}
            className="min-w-[48px] min-h-[48px] p-3 rounded-full bg-white border border-slate-300 shadow-md hover:bg-slate-100 text-slate-800 transition active:scale-95 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-orange-500"
            aria-label="Témoignage suivant"
          >
            <ChevronRight className="w-6 h-6 text-slate-900" />
          </button>
        </div>
      </div>
    </div>
  );
};
